import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Alert, Grid,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export function TournamentMergeDivisions() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTid, setSelectedTid] = useState('');
  const [fromDiv, setFromDiv] = useState('');
  const [toDiv, setToDiv] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'tournaments'), orderBy('date', 'desc')))
      .then(snap => setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const selectedT = tournaments.find(t => t.id === selectedTid);
  const divisions = selectedT
    ? (Array.isArray(selectedT.divisions) ? selectedT.divisions : JSON.parse(selectedT.divisions || '[]'))
    : [];

  async function handleMerge(e) {
    e.preventDefault();
    if (fromDiv === toDiv) { alert('Select different divisions.'); return; }
    if (!window.confirm(`Move ALL registrations from "${fromDiv}" into "${toDiv}"?\n\nThis cannot be automatically undone.`)) return;
    setMerging(true);
    try {
      const snap = await getDocs(query(collection(db, 'registrations'),
        where('tournament_id', '==', selectedTid), where('division', '==', fromDiv)));
      await Promise.all(snap.docs.map(d => updateDoc(doc(db, 'registrations', d.id), { division: toDiv })));
      alert(`Merged ${snap.size} registration(s) from "${fromDiv}" into "${toDiv}".`);
      navigate('/tournament-admin/registrations');
    } catch(e) { alert('Merge failed: ' + e.message); }
    finally { setMerging(false); }
  }

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold">⇄ Merge Divisions</Typography>
          <Button variant="outlined" color="inherit" startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/tournament-admin/registrations')}>Back to Registrations</Button>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ pb: 6 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Bulk operation:</strong> All registrations in the source division will be moved to the destination. This cannot be automatically undone.
        </Alert>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleMerge}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Tournament</InputLabel>
                <Select value={selectedTid} label="Tournament" required
                        onChange={e => { setSelectedTid(e.target.value); setFromDiv(''); setToDiv(''); }}>
                  <MenuItem value="">— Select Tournament —</MenuItem>
                  {tournaments.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
              {divisions.length > 0 && (
                <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
                  <Grid size={{ xs: 5 }}>
                    <FormControl fullWidth>
                      <InputLabel>From Division</InputLabel>
                      <Select value={fromDiv} label="From Division" required onChange={e => setFromDiv(e.target.value)}>
                        <MenuItem value="">— select —</MenuItem>
                        {divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Will be emptied</Typography>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 2 }} sx={{ textAlign: 'center', pt: 1 }}>
                    <ArrowForwardIcon color="disabled" sx={{ fontSize: 32 }} />
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    <FormControl fullWidth>
                      <InputLabel>Into Division</InputLabel>
                      <Select value={toDiv} label="Into Division" required onChange={e => setToDiv(e.target.value)}>
                        <MenuItem value="">— select —</MenuItem>
                        {divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Will receive all teams</Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              <Button type="submit" variant="contained" color="warning" startIcon={merging ? null : <MergeIcon />} disabled={merging}>
                {merging ? <CircularProgress size={20} color="inherit" /> : 'Merge Divisions'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export function TournamentExportCSV() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTid, setSelectedTid] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [selectedDiv, setSelectedDiv] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'tournaments'), orderBy('date', 'desc')))
      .then(snap => setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  function onTournamentChange(tid) {
    setSelectedTid(tid); setSelectedDiv('');
    if (!tid) { setDivisions([]); return; }
    const t = tournaments.find(t => t.id === tid);
    if (t) setDivisions(Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]'));
  }

  async function handleExport(e) {
    e.preventDefault();
    setExporting(true);
    try {
      const snap = await getDocs(query(collection(db, 'registrations'),
        where('tournament_id', '==', selectedTid), where('division', '==', selectedDiv), where('status', '==', 'active')));
      const lines = ['Names,Points'];
      snap.docs.map(d => d.data()).forEach(r => {
        const p1 = (r.first_name || '').trim();
        const p2First = (r.partner_name || '').split(' ')[0] || '';
        lines.push(`"${p1}${p2First ? ' / '+p2First : ''}",""`);
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const tName = (tournaments.find(t => t.id === selectedTid)?.name || 'tournament').replace(/\s+/g,'_');
      a.href = url; a.download = `${tName}_${selectedDiv.replace(/\s+/g,'_')}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Export failed: ' + e.message); }
    finally { setExporting(false); }
  }

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container><Typography variant="h4" fontWeight="bold">⬇ Export CSV for VBS</Typography></Container>
      </Box>
      <Container maxWidth="sm" sx={{ pb: 6 }}>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Exports a CSV with <strong>Names</strong> and <strong>Points</strong> columns, formatted for import into vb-scores.
            </Typography>
            <form onSubmit={handleExport}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Tournament</InputLabel>
                <Select value={selectedTid} label="Tournament" required onChange={e => onTournamentChange(e.target.value)}>
                  <MenuItem value="">— Select Tournament —</MenuItem>
                  {tournaments.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
              {divisions.length > 0 && (
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel>Division</InputLabel>
                  <Select value={selectedDiv} label="Division" required onChange={e => setSelectedDiv(e.target.value)}>
                    <MenuItem value="">— Select Division —</MenuItem>
                    {divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <Button type="submit" variant="contained" color="success" startIcon={exporting ? null : <DownloadIcon />} disabled={exporting}>
                {exporting ? <CircularProgress size={20} color="inherit" /> : 'Download CSV'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>Back to Dashboard</Button>
        </Box>
      </Container>
    </Box>
  );
}
